import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { DataGrid, GridRowModes, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { apiUrl } from '../utils/api';
import { dataGridSx } from '../utils/dataGridStyles';
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Snackbar,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  MenuItem,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import StraightenIcon from '@mui/icons-material/Straighten';

const Units = ({ access = 'none', deleteAllAccess = 'none' }) => {
  const readOnly = access !== 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [newUnit, setNewUnit] = useState({
    unit_code: '',
    unit_description: ''
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const UNIT_FIELDS = ['Unit Code', 'Unit Description'];
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [excelImporting, setExcelImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const fileInputRef = React.useRef(null);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const fetchUnits = async () => {
    if (access === 'none') return;
    
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/units'));
      if (!response.ok) throw new Error('Failed to fetch units');
      
      const data = await response.json();
      setUnits(data);
    } catch (error) {
      console.error('Error fetching units:', error);
      showNotification('Failed to load units', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [access]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!excelImporting) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 300);
    return () => clearInterval(timer);
  }, [excelImporting]);

  const handleAdd = async () => {
    if (readOnly || !newUnit.unit_code.trim() || !newUnit.unit_description.trim()) return;
    
    try {
      const response = await fetch(apiUrl('/api/units'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUnit)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create unit');
      }
      
      setNewUnit({ unit_code: '', unit_description: '' });
      fetchUnits();
      showNotification('Unit created successfully');
    } catch (error) {
      console.error('Error creating unit:', error);
      showNotification(error.message, 'error');
    }
  };

  const handleDeleteClick = (id) => () => {
    if (readOnly) return;
    const unit = units.find(u => u.unit_id === id);
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!unitToDelete) return;

    try {
      const response = await fetch(apiUrl(`/api/units/${unitToDelete.unit_id}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete unit');
      }

      fetchUnits();
      showNotification('Unit deleted successfully');
      setDeleteDialogOpen(false);
      setUnitToDelete(null);
    } catch (error) {
      console.error('Error deleting unit:', error);
      showNotification(error.message, 'error');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUnitToDelete(null);
  };

  // Delete all
  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    setDeletingAll(true);
    try {
      const response = await fetch(apiUrl('/api/units/delete-all'), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete all units');
      }

      fetchUnits();
      showNotification('All units deleted successfully', 'success');
      setDeleteAllDialogOpen(false);
    } catch (error) {
      console.error('Error deleting all units:', error);
      showNotification(error.message, 'error');
      setDeleteAllDialogOpen(false);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  // Export template
  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/units/template`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'units_template.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showNotification('Failed to download template', 'error'));
  };

  // Export to Excel
  const handleExport = () => {
    const XLSX = require('xlsx');
    const rows = units.map(unit => ({
      'Unit Code': unit.unit_code || '',
      'Unit Description': unit.unit_description || ''
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Units');
    XLSX.writeFile(wb, 'units.xlsx');
  };

  // Import from Excel
  const handleImportClick = () => {
    if (readOnly) return;
    setImportSourceOpen(true);
  };

  const handleImportSource = (isThird: boolean) => {
    setImportSourceOpen(false);
    if (isThird) {
      setMapFile(null);
      setFileHeaders([]);
      setMapping({});
      setMappingOpen(true);
    } else { fileInputRef.current?.click(); }
  };

  const handleMapFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileHeaders(headers);
      const auto: Record<string, string> = { ...mapping };
      UNIT_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g, '_') === fld.toLowerCase().replace(/\s+/g, '_'));
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMapping(auto);
    } catch { setFileHeaders([]); }
  };

  const handleMappingSubmit = async () => {
    if (!mapFile) { alert('Select a file'); return; }
    try {
      const buf = await mapFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as any[];
      const norm = rows.map(r => UNIT_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: (r as any)[mapping[fld]] ?? '' }), {}));
      const newWb = XLSX.utils.book_new();
      const newWs = XLSX.utils.json_to_sheet(norm);
      XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
      const buf2 = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const mappedFile = new File([blob], 'mapped.xlsx');
      setMappingOpen(false);
      await runImport(mappedFile);
    } catch (err: any) { alert(err.message || 'Mapping import failed'); }
  };

  const runImport = async (file: File) => {
    try {
      setExcelImporting(true);
      setImportProgress(0);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(apiUrl(`/api/units/import?confirm=true&overwrite=true`), {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to import units');
      const added = data.created || 0;
      const overwritten = data.updated || 0;
      const skipped = data.skipped || 0;
      const failed = (data.errors || []).length;
      const logRows = [
        ...Array(added).fill(null).map((_: any, i: number) => ({ label: `Unit ${i + 1}`, status: 'Added' })),
        ...Array(overwritten).fill(null).map((_: any, i: number) => ({ label: `Unit ${added + i + 1}`, status: 'Overwritten' })),
        ...Array(skipped).fill(null).map((_: any, i: number) => ({ label: `Existing Unit ${i + 1}`, status: 'Skipped' })),
        ...(data.errors || []).map((e: any) => ({ label: `Row ${e.row || '?'}`, status: `Failed: ${e.field ? e.field + ' - ' : ''}${e.error || e}` }))
      ];
      setImportProgress(100);
      setImportSummaryData({ total: added + overwritten + skipped + failed, added, overwritten, skipped, failed, rows: logRows });
      setImportSummaryOpen(true);
      fetchUnits();
    } catch (err: any) {
      setImportProgress(0);
      setImportSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, error: err.message || 'Import failed', rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportSummaryOpen(true);
      showNotification(err.message || 'Import failed', 'error');
    } finally { setExcelImporting(false); }
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s === 'Skipped') { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, unit: r.label, status, notes }
    })
    fetch(apiUrl('/api/units/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'Unit_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    runImport(file);
  };

  const handleEditClick = (id) => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    if (readOnly) return;
    setRowModesModel({ 
      ...rowModesModel, 
      [id]: { mode: GridRowModes.View, ignoreModifications: true } 
    });
  };

  const processRowUpdate = async (updatedRow) => {
    const { unit_id, unit_code, unit_description } = updatedRow;
    
    try {
      const response = await fetch(apiUrl(`/api/units/${unit_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unit_code, unit_description })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update unit');
      }
      
      fetchUnits();
      showNotification('Unit updated successfully');
      return updatedRow;
    } catch (error) {
      console.error('Error updating unit:', error);
      showNotification(error.message, 'error');
      throw error;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'unit_id',
      headerName: 'ID',
      width: 90,
      type: 'number'
    },
    { 
      field: 'unit_code', 
      headerName: 'Unit Code', 
      flex: 1, 
      editable: !readOnly,
      minWidth: 150
    },
    { 
      field: 'unit_description', 
      headerName: 'Description', 
      flex: 2, 
      editable: !readOnly,
      minWidth: 200
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'dateTime',
      valueGetter: (params: any) => {
        if (!params || !params.value) return null;
        try {
          return new Date(params.value);
        } catch (e) {
          return null;
        }
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id }) => {
        if (readOnly) return [];
        
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        
        if (isEditing) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={handleSaveClick(id)}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={handleCancelClick(id)}
              showInMenu={false}
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
            label="Edit"
            onClick={handleEditClick(id)}
            showInMenu={false}
          />,
          <GridActionsCellItem
            icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
            label="Delete"
            onClick={handleDeleteClick(id)}
            showInMenu={false}
          />
        ];
      }
    }
  ];

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {!readOnly && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add New Unit
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Unit Code"
                  value={newUnit.unit_code}
                  onChange={(e) => setNewUnit({ ...newUnit, unit_code: e.target.value })}
                  placeholder="e.g., sqm, lm, pcs"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newUnit.unit_description}
                  onChange={(e) => setNewUnit({ ...newUnit, unit_description: e.target.value })}
                  placeholder="e.g., Square Meter, Linear Meter, Pieces"
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAdd}
                  disabled={!newUnit.unit_code.trim() || !newUnit.unit_description.trim()}
                  startIcon={<AddIcon />}
                >
                  Add Unit
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={units}
            columns={columns}
            getRowId={(row) => row.unit_id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}
            loading={loading}
            autoHeight
            columnVisibilityModel={{
              unit_id: false,
              created_at: false
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 }
              }
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={dataGridSx}
          />
        </CardContent>
      </Card>

      {/* Import/Export Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Export Template
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export To Excel
        </Button>
        <Button variant="contained" onClick={handleImportClick} disabled={readOnly}>
          Import From Excel
        </Button>
        {canDeleteAll && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllClick}
            disabled={deletingAll || loading}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
        />
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity as AlertColor}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete Unit
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone! All BOQ line items using this unit will also be deleted.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the following unit?
          </Typography>
          {unitToDelete && (
            <Box sx={{
              bgcolor: 'grey.50',
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.300'
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Unit Code
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5 }}>
                {unitToDelete.unit_code}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Description
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {unitToDelete.unit_description}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Unit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Source Dialog */}
      <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
        <DialogTitle>Import From Excel</DialogTitle>
        <DialogContent>Choose source type:</DialogContent>
        <DialogActions>
          <Button onClick={() => handleImportSource(false)}>Template</Button>
          <Button onClick={() => handleImportSource(true)}>Third-party (Map Columns)</Button>
          <Button onClick={() => setImportSourceOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog open={mappingOpen} onClose={() => { setMappingOpen(false); setFileHeaders([]); setMapFile(null); setMapping({}); }} maxWidth="md" fullWidth>
        <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 8 }}>
            <input type="file" accept=".xlsx,.xls" onChange={handleMapFileChange} />
          </div>
          {!!fileHeaders.length && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {UNIT_FIELDS.map(f => (
                <div key={f}>
                  <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                  <TextField select size="small" fullWidth value={mapping[f] || ''} onChange={(e) => setMapping(prev => ({ ...prev, [f]: e.target.value }))}>
                    <MenuItem value="">(none)</MenuItem>
                    {fileHeaders.map(h => <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>)}
                  </TextField>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
          <Button onClick={handleMappingSubmit} disabled={!mapFile}>Import</Button>
        </DialogActions>
      </Dialog>

      {/* Importing Progress Dialog */}
      <Dialog open={excelImporting}>
        <DialogTitle>Importing...</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <LinearProgress variant="determinate" value={importProgress} />
          <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
        </DialogContent>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{importSummaryData?.error ? 'Import Failed' : 'Import Summary'}</DialogTitle>
        <DialogContent>
          {importSummaryData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>Total units processed: <strong>{importSummaryData.total}</strong></Typography>
              <Typography variant="body2" sx={{ color: 'green' }}>Added: {importSummaryData.added}</Typography>
              <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importSummaryData.overwritten}</Typography>
              <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importSummaryData.skipped}</Typography>
              <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importSummaryData.failed}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importSummaryData.added + importSummaryData.overwritten}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => downloadImportLog(importSummaryData)} variant="outlined">Download Import Log</Button>
          <Button onClick={() => setImportSummaryOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete All Units
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>ALL</strong> units?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove all units from the database. All BOQ line items using these units will also be affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteAllCancel} variant="outlined" disabled={deletingAll}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deletingAll}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Units;
